/**
 * Adapters from the Info hub API shapes (server/info/types) to the legacy V2 Info types still used
 * by older consumers (Swap hot-token list, watchlists). Unavailable values become `undefined`
 * (rendered as "-"), never 0.
 */
import type { PoolStat, ProtocolStat, TokenStat } from 'server/info/types'
import { getLpFeesAndApr } from 'utils/getLpFeesAndApr'
import type { PoolData, ProtocolData, TokenData } from './types'

const u = (v: number | null | undefined): number => (v === null || v === undefined ? (undefined as unknown as number) : v)

export const poolStatToPoolData = (p: PoolStat): PoolData => {
  const fees = getLpFeesAndApr(p.volume24hUSD ?? 0, p.volume7dUSD ?? 0, p.tvlUSD ?? 0)
  return {
    address: p.address,
    token0: { name: p.token0.name, symbol: p.token0.symbol, address: p.token0.address },
    token1: { name: p.token1.name, symbol: p.token1.symbol, address: p.token1.address },
    volumeUSD: u(p.volume24hUSD),
    volumeUSDChange: u(p.volume24hChange),
    volumeUSDWeek: u(p.volume7dUSD),
    volumeUSDChangeWeek: u(p.volume7dChange),
    totalFees24h: p.volume24hUSD === null ? u(null) : fees.totalFees24h,
    totalFees7d: p.volume7dUSD === null ? u(null) : fees.totalFees7d,
    lpFees24h: p.volume24hUSD === null ? u(null) : fees.lpFees24h,
    lpFees7d: p.volume7dUSD === null ? u(null) : fees.lpFees7d,
    lpApr7d: p.volume7dUSD === null || p.tvlUSD === null ? u(null) : fees.lpApr7d,
    liquidityUSD: u(p.tvlUSD),
    liquidityUSDChange: u((p as any).tvlChange24h ?? null),
    token0Price: p.token0Price,
    token1Price: p.token1Price,
    liquidityToken0: p.reserve0,
    liquidityToken1: p.reserve1,
  }
}

export const tokenStatToTokenData = (t: TokenStat): TokenData => ({
  exists: true,
  name: t.name,
  symbol: t.symbol,
  address: t.address,
  decimals: t.decimals,
  volumeUSD: u(t.volume24hUSD),
  volumeUSDChange: u(t.volume24hChange),
  volumeUSDWeek: u(t.volume7dUSD),
  txCount: t.txCount24h,
  liquidityToken: t.liquidityToken,
  liquidityUSD: u(t.liquidityUSD),
  liquidityUSDChange: u(null),
  priceUSD: u(t.priceUSD),
  priceUSDChange: u(t.priceChange24h),
  priceUSDChangeWeek: u(null),
})

export const protocolStatToProtocolData = (p: ProtocolStat): ProtocolData => ({
  volumeUSD: u(p.volume24hUSD),
  volumeUSDChange: u(p.volume24hChange),
  liquidityUSD: u(p.tvlUSD),
  liquidityUSDChange: u(p.tvlChange24h),
  txCount: p.txCount24h,
  txCountChange: u(p.txCount24hChange),
})
